import { EntitiyDefinition, FieldDefinitions } from '../DataProvider';
import { EMode } from '../Enums/EMode.enum';
import { Entities } from '../Enums/Entities.enum';
import { ServiceContractDeductionKind } from '../Enums/ServiceContractDeductionKind.enum';
import { ServiceContractRythm } from '../Enums/ServiceContractRythm.enum';

import { AdditionalAddress } from './AdditionalAddress.entity';
import { BaseDataLanguage } from './BaseDataLanguage.entity';
import { Contract } from './Contract.entity';
import { ContractGroup } from './ContractGroup.entity';
import { CostCenter } from './CostCenter.entity';
import { CustomField } from './CustomField.entity';
import { File } from './File.entity';
import { LookUp } from './LookUp.entity';
import { PaymentKind } from './PaymentKind.entity';
import { PriceGroup } from './PriceGroup.entity';
import { Project } from './Project.entity';
import { ServiceContractPosition } from './ServiceContractPosition.entity';
import { TaxGroup } from './TaxGroup.entity';

export class ServiceContract<T extends EMode = EMode.entity> {
  /** Alias: abrechnungArt */
  serviceContractDeductionKind?: ServiceContractDeductionKind;
  /** Alias: artLookupCode */
  kindLookupId?: number;
  /** Alias: auftragCode */
  contractId?: number;
  /** Alias: orderConfirmation */
  contract?: Contract<T>;
  /** Alias: betrag */
  amount?: number;
  /** Alias: code */
  id?: number;
  /** Alias: creationBenutzerCode */
  creatorUserId?: number;
  /** Alias: creationDate */
  creationDate?: string;
  /** Alias: datumKuendigung */
  terminationDate?: string;
  /** Alias: datumNaechsteAbrechnung */
  nextInvoiceDate?: string;
  /** Alias: dauerrechnung */
  permanentInvoice?: boolean;
  /** Alias: endKundeApCode */
  endCustomerContactId?: number;
  /** Alias: endkundeSdObjMemberCode */
  endCustomerSdObjMemberId?: number;
  /** Alias: grCode */
  groupId?: number;
  /** Alias: hauptadresseAbteilungCode */
  mainAddressDepartmentId?: number;
  /** Alias: hauptadresseApCode */
  mainAddressContactId?: number;
  /** Alias: hauptadresseSdObjMemberCode */
  mainAddressSdObjMemberId?: number;
  /** Alias: insertTime */
  insertTime?: string;
  /** Alias: kostenstelleCode */
  costCenterId?: number;
  /** Alias: name */
  name?: string;
  /** Alias: nebenadresse1AbteilungCode */
  deliveryAddressDepartmentId?: number;
  /** Alias: nebenadresse1ApCode */
  deliveryAddressContactId?: number;
  /** Alias: nebenadresse1SdObjMemberCode */
  deliveryAddressSdObjMemberId?: number;
  /** Alias: nebenadresse2AbteilungCode */
  additionalAddress2DepartmentId?: number;
  /** Alias: nebenadresse2ApCode */
  additionalAddress2ContactId?: number;
  /** Alias: nebenadresse2SdObjMemberCode */
  additionalAddress2SdObjMemberId?: number;
  /** Alias: notiz */
  note?: string;
  /** Alias: nummer */
  number?: number;
  /** Alias: positionen */
  positionList?: ServiceContractPosition<T>[];
  /** Alias: rhytmus */
  rythm?: ServiceContractRythm;
  /** Alias: skonto */
  skonto?: number;
  /** Alias: skontoFrist */
  skontoDurationDays?: number;
  /** Alias: spracheCode */
  languageId?: number;
  /** Alias: updateTime */
  updateTime?: string;
  /** Alias: wartungsvertragEnde */
  serviceContractEndDate?: string;
  /** Alias: wartungsvertragNummer */
  serviceContractNumber?: string;
  /** Alias: wartunsgvertragBegin */
  serviceContractStartDate?: string;
  /** Alias: zahlungCode */
  paymentId?: number;
  /** Alias: zahlungsfrist */
  paymentDeadline?: number;
  /** Alias: customFields */
  customFieldList?: CustomField<T>[];
  /** Alias: mainAddress */
  mainAddress?: AdditionalAddress<T>;
  /** Alias: projektCode */
  projectId?: number;
  /** Alias: project */
  project?: Project<T>;
  /** Alias: endCustomer */
  endCustomer?: AdditionalAddress<T>;
  /** Alias: deliveryAddress */
  deliveryAddress?: AdditionalAddress<T>;
  /** Alias: singlePriceTotal */
  singlePriceTotal?: number;
  /** Alias: priceTotal */
  priceTotal?: number;
  /** Alias: group */
  group?: ContractGroup<T>;
  /** Alias: typeOfContract */
  typeOfContract?: LookUp<T>;
  /** Alias: eRechnung_Projektreferenz_BT11 */
  eInvoice_ProjectReference_BT11?: string;
  /** Alias: eRechnung_Vertragsnummer_BT12 */
  eInvoice_ContractNumber_BT12?: string;
  /** Alias: eRechnung_Bestellnummer_BT13 */
  eInvoice_OrderNumber_BT13?: string;
  /** Alias: eRechnung_Auftragsnummer_BT14 */
  eInvoice_OrderConfirmationNumber_BT14?: string;
  /** Alias: eRechnung_Empfangsbestaetigung_BT15 */
  eInvoice_ConfirmtaionOfReceipt_BT15?: string;
  /** Alias: eRechnung_Versandanzeige_BT16 */
  eInvoice_SendDisplay_BT16?: string;
  /** Alias: eRechnung_Tender_BT17 */
  eInvoice_Tender_BT17?: string;
  /** Alias: eRechnung_Objekt_BT18 */
  eInvoice_Object_BT18?: string;
  /** Alias: erstbelegnummer */
  firstReceiptNumber?: number;
  /** Alias: priceGroupCode */
  priceGroupId?: number;
  /** Alias: includedMinutesPerMonth */
  includedMinutesPerMonth?: string;
  /** Alias: preisgruppeItem */
  priceGroup?: PriceGroup<T>;
  /** Alias: attachements */
  attachements?: File<T>[];
  /** Alias: taxGroupCode */
  taxGroupId?: number;
  /** Alias: language */
  language?: BaseDataLanguage<T>;
  /** Alias: costCenter */
  costCenter?: CostCenter<T>;
  /** Alias: paymentType */
  paymentKind?: PaymentKind<T>;
  /** Alias: taxGroup */
  taxGroup?: TaxGroup<T>;
  /** Alias: addition */
  addition?: string;

  __typename?: string;
}

const fields: FieldDefinitions<ServiceContract> = {
  serviceContractDeductionKind: {
    alias: 'abrechnungArt',
  },
  kindLookupId: {
    alias: 'artLookupCode',
  },
  contractId: {
    alias: 'auftragCode',
  },
  amount: {
    alias: 'betrag',
  },
  id: {
    alias: 'code',
  },
  creatorUserId: {
    alias: 'creationBenutzerCode',
  },
  creationDate: {
    alias: 'creationDate',
  },
  terminationDate: {
    alias: 'datumKuendigung',
  },
  nextInvoiceDate: {
    alias: 'datumNaechsteAbrechnung',
  },
  permanentInvoice: {
    alias: 'dauerrechnung',
  },
  endCustomerContactId: {
    alias: 'endKundeApCode',
  },
  endCustomerSdObjMemberId: {
    alias: 'endkundeSdObjMemberCode',
  },
  groupId: {
    alias: 'grCode',
  },
  mainAddressDepartmentId: {
    alias: 'hauptadresseAbteilungCode',
  },
  mainAddressContactId: {
    alias: 'hauptadresseApCode',
  },
  mainAddressSdObjMemberId: {
    alias: 'hauptadresseSdObjMemberCode',
  },
  insertTime: {
    alias: 'insertTime',
  },
  costCenterId: {
    alias: 'kostenstelleCode',
  },
  name: {
    alias: 'name',
  },
  deliveryAddressDepartmentId: {
    alias: 'nebenadresse1AbteilungCode',
  },
  deliveryAddressContactId: {
    alias: 'nebenadresse1ApCode',
  },
  deliveryAddressSdObjMemberId: {
    alias: 'nebenadresse1SdObjMemberCode',
  },
  additionalAddress2DepartmentId: {
    alias: 'nebenadresse2AbteilungCode',
  },
  additionalAddress2ContactId: {
    alias: 'nebenadresse2ApCode',
  },
  additionalAddress2SdObjMemberId: {
    alias: 'nebenadresse2SdObjMemberCode',
  },
  note: {
    alias: 'notiz',
  },
  number: {
    alias: 'nummer',
  },
  positionList: {
    alias: 'positionen',
    entity: Entities.serviceContractPosition,
  },
  rythm: {
    alias: 'rhytmus',
  },
  skonto: {
    alias: 'skonto',
  },
  skontoDurationDays: {
    alias: 'skontoFrist',
  },
  languageId: {
    alias: 'spracheCode',
  },
  updateTime: {
    alias: 'updateTime',
  },
  serviceContractEndDate: {
    alias: 'wartungsvertragEnde',
  },
  serviceContractNumber: {
    alias: 'wartungsvertragNummer',
  },
  serviceContractStartDate: {
    alias: 'wartunsgvertragBegin',
  },
  paymentId: {
    alias: 'zahlungCode',
  },
  paymentDeadline: {
    alias: 'zahlungsfrist',
  },
  customFieldList: {
    alias: 'customFields',
    entity: Entities.customField,
  },
  mainAddress: {
    alias: 'mainAddress',
    entity: Entities.additionalAddress,
  },
  projectId: {
    alias: 'projektCode',
  },
  project: {
    alias: 'project',
    entity: Entities.project,
  },
  endCustomer: {
    alias: 'endCustomer',
    entity: Entities.additionalAddress,
  },
  deliveryAddress: {
    alias: 'deliveryAddress',
    entity: Entities.additionalAddress,
  },
  singlePriceTotal: {
    alias: 'singlePriceTotal',
  },
  priceTotal: {
    alias: 'priceTotal',
  },
  group: {
    alias: 'group',
    entity: Entities.contractGroup,
  },
  typeOfContract: {
    alias: 'typeOfContract',
    entity: Entities.lookUp,
  },
  eInvoice_ProjectReference_BT11: {
    alias: 'eRechnung_Projektreferenz_BT11',
  },
  eInvoice_ContractNumber_BT12: {
    alias: 'eRechnung_Vertragsnummer_BT12',
  },
  eInvoice_OrderNumber_BT13: {
    alias: 'eRechnung_Bestellnummer_BT13',
  },
  eInvoice_OrderConfirmationNumber_BT14: {
    alias: 'eRechnung_Auftragsnummer_BT14',
  },
  eInvoice_ConfirmtaionOfReceipt_BT15: {
    alias: 'eRechnung_Empfangsbestaetigung_BT15',
  },
  eInvoice_SendDisplay_BT16: {
    alias: 'eRechnung_Versandanzeige_BT16',
  },
  eInvoice_Tender_BT17: {
    alias: 'eRechnung_Tender_BT17',
  },
  eInvoice_Object_BT18: {
    alias: 'eRechnung_Objekt_BT18',
  },
  firstReceiptNumber: {
    alias: 'erstbelegnummer',
  },
  priceGroupId: {
    alias: 'priceGroupCode',
  },
  includedMinutesPerMonth: {
    alias: 'includedMinutesPerMonth',
  },
  priceGroup: {
    alias: 'preisgruppeItem',
    entity: Entities.priceGroup,
  },
  attachements: {
    alias: 'attachements',
    entity: Entities.file,
  },
  taxGroupId: {
    alias: 'taxGroupCode',
  },
  language: {
    alias: 'language',
    entity: Entities.baseDataLanguage,
  },
  costCenter: {
    alias: 'costCenter',
    entity: Entities.costCenter,
  },
  paymentKind: {
    alias: 'paymentType',
    entity: Entities.paymentKind,
  },
  taxGroup: {
    alias: 'taxGroup',
    entity: Entities.taxGroup,
  },
  contract: {
    alias: 'orderConfirmation',
    entity: Entities.contract,
  },
  addition: {
    alias: 'addition',
  },
  __typename: {
    alias: '__typename',
  },
};

export const serviceContractEntityDefinition: EntitiyDefinition<ServiceContract> =
  {
    local: {},
    remote: {
      queryName: 'getWartungsvertrag',
      fragmentName: 'Wartungsvertrag',
      withPaginationWrapper: true,
      params: [
        { name: 'querySize', type: 'Int' },
        { name: 'queryPage', type: 'Int' },
        { name: 'querySortOrder', type: 'SortOrderStyle' },
        { name: 'querySortBy', type: 'String' },
        { name: 'querySortByEx', type: '[SortByPair]' },
        { name: 'filter', type: 'String' },
      ],
    },
    fieldDefinitions: fields,
  };
